import {
  IPE_AGENT_CHAT_PATH,
  IPE_CREDIT_CONSUMPTION_PATH,
  IPE_RECHARGE_PATH,
  IPE_MANUAL_HOME_PATH,
  IPE_MANUAL_LOGIN_PATH,
  IPE_PLUGIN_INSTALL_PATH,
  isIpeLoginPath,
  isIpeWorkbenchPath,
} from '@/config/ipeRoutes';
import {
  IPE_SHEIN_BASE_PATH,
  IPE_SHEIN_GATE_PATH,
  SHEIN_NAV_ITEMS,
  SHEIN_PRODUCT_SUBNAV,
  SHEIN_COMPLIANCE_SUBNAV,
  SHEIN_TEMPLATE_SUBNAV,
  isSheinPlatformPath,
} from '@/shein-platform/constants/sheinRoutes';
import {
  IPE_TEMU_BASE_PATH,
  IPE_TEMU_GATE_PATH,
  isTemuPlatformPath,
  resolveTemuPageMeta,
} from '@/temu-platform/constants/temuRoutes';
import {
  IPE_TIKTOK_BASE_PATH,
  IPE_TIKTOK_GATE_PATH,
  isTiktokPlatformPath,
  resolveTiktokPageMeta,
} from '@/tiktok-platform/constants/tiktokRoutes';
import {
  NAV_HUBS,
  TOOL_NAV_ITEMS,
  WORKBENCH_HOME_NAV_ID,
  resolveNavFromWorkbenchLocation,
} from '@/pages/ipe/workbench/constants/workbenchNavConfig';
import i18n from '@/i18n';
import { fixedUi } from '@/i18n/fixedUi';
import { platformNavLabel } from '@/i18n/platformNavLabel';

export const DEFAULT_DOCUMENT_TITLE = 'IPE';

const WORKBENCH_DETAIL_TITLES = [
  ['workflow-template-edit', '工作流模板编辑'],
  ['workflow-batch-run', '工作流批量执行'],
  ['image-template-editor', '套图模板编辑'],
  ['image-designer', '套图设计器'],
  ['listing-template-preview', '刊登模板预览'],
  ['goods-detail', 'POD商品设计详情'],
  ['goods-composite-edit', '主图合成编辑'],
  ['pod-composite-edit', 'POD 合成编辑'],
  ['product-listing-detail', '商品详情'],
  ['listing-job-detail', '刊登任务详情'],
  ['image-crop-detail', '图案裁剪详情'],
  ['image-enhance-detail', '超分提质详情'],
  ['smart-cutout-detail', '智能抠图详情'],
  ['video-generate-detail', '视频任务详情'],
  ['img2img-detail', '万能改图详情'],
  ['ai-image-detail', 'AI 图片详情'],
  ['ai-model-detail', 'AI 模特详情'],
  ['model-try-on-detail', '模特试衣详情'],
  ['model-handheld-detail', '手持商品详情'],
  ['model-pose-detail', '姿势裂变详情'],
  ['model-wear-detail', '万物穿戴详情'],
  ['ai-copywriting-detail', 'AI 文案详情'],
  ['ai-outpaint-detail', 'AI 扩图详情'],
  ['ai-text-to-image-detail', '文生图详情'],
  ['stamp-extract-detail', '印花提取详情'],
  ['image-fission-detail', '图片裂变详情'],
  ['insights', '市场洞察'],
];

export function formatIpeDocumentTitle(pageTitle) {
  const trimmed = String(pageTitle || '').trim();
  if (!trimmed || trimmed === DEFAULT_DOCUMENT_TITLE) return DEFAULT_DOCUMENT_TITLE;
  return `${DEFAULT_DOCUMENT_TITLE} · ${trimmed}`;
}

function navTitle(item) {
  if (!item) return '';
  return platformNavLabel(
    (key, opts) => i18n.t(key, { ns: 'workbench', defaultValue: '', ...opts }),
    item,
  );
}

function resolveSheinDocumentTitle(pathname = '') {
  if (pathname === IPE_SHEIN_GATE_PATH || pathname.startsWith(`${IPE_SHEIN_GATE_PATH}?`)) {
    return formatIpeDocumentTitle(fixedUi('进入 SHEIN 工作区'));
  }

  const rest = pathname.startsWith(`${IPE_SHEIN_BASE_PATH}/`)
    ? pathname.slice(IPE_SHEIN_BASE_PATH.length + 1)
    : '';
  let segments = rest.split('/').filter(Boolean);
  // 跳过店铺类型路径段 full|semi|self
  if (segments[0] === 'full' || segments[0] === 'semi' || segments[0] === 'self') {
    segments = segments.slice(1);
  }
  const top = segments[0] || '';

  // 产品管理子页：浏览器标签跟随具体页面，勿一律「产品管理」
  if (top === 'products') {
    const child = segments[1] || '';
    if (!child) {
      return formatIpeDocumentTitle(`SHEIN · ${navTitle({ label: '产品信息', labelKey: 'productInfo', labelFallback: '产品信息' })}`);
    }
    if (child === 'edit') {
      return formatIpeDocumentTitle(`SHEIN · ${fixedUi('编辑产品')}`);
    }
    const sub = SHEIN_PRODUCT_SUBNAV.find((item) => item.path === child);
    if (sub?.label) {
      return formatIpeDocumentTitle(`SHEIN · ${navTitle(sub)}`);
    }
    return formatIpeDocumentTitle(`SHEIN · ${navTitle({ label: '产品管理', labelKey: 'products', labelFallback: '产品管理' })}`);
  }

  if (top === 'templates') {
    const child = segments[1] || '';
    if (child === 'product' && (segments[2] === 'new' || segments[2] === 'edit')) {
      return formatIpeDocumentTitle(
        segments[2] === 'edit' ? `SHEIN · ${fixedUi('编辑产品模板')}` : `SHEIN · ${fixedUi('新建产品模板')}`,
      );
    }
    const sub = SHEIN_TEMPLATE_SUBNAV.find((item) => item.path === child);
    if (sub?.label) {
      return formatIpeDocumentTitle(`SHEIN · ${navTitle(sub)}`);
    }
    return formatIpeDocumentTitle(`SHEIN · ${navTitle({ label: '模板库', labelKey: 'templates', labelFallback: '模板库' })}`);
  }

  if (top === 'compliance') {
    const child = segments[1] || '';
    const sub = SHEIN_COMPLIANCE_SUBNAV.find((item) => item.path === child);
    if (sub?.label) {
      return formatIpeDocumentTitle(`SHEIN · ${navTitle(sub)}`);
    }
    return formatIpeDocumentTitle(`SHEIN · ${navTitle({ label: '合规资质', labelKey: 'complianceQual', labelFallback: '合规资质' })}`);
  }

  const navItem = SHEIN_NAV_ITEMS.find((item) => item.path === top);
  if (navItem) {
    return formatIpeDocumentTitle(`SHEIN · ${navTitle(navItem)}`);
  }

  if (pathname === IPE_SHEIN_BASE_PATH) {
    return formatIpeDocumentTitle(fixedUi('电商平台'));
  }

  return formatIpeDocumentTitle(fixedUi('电商平台'));
}

function resolveTemuDocumentTitle(pathname = '') {
  if (pathname === IPE_TEMU_GATE_PATH || pathname.startsWith(`${IPE_TEMU_GATE_PATH}?`)) {
    return formatIpeDocumentTitle(fixedUi('进入 TEMU 工作区'));
  }
  if (pathname === IPE_TEMU_BASE_PATH) {
    return formatIpeDocumentTitle(fixedUi('TEMU 工作区'));
  }
  const match = pathname.match(/^\/ipe\/temu\/(full|semi|local)(\/.*)?$/);
  if (!match) {
    return formatIpeDocumentTitle(fixedUi('TEMU 工作区'));
  }
  const track = match[1];
  const meta = resolveTemuPageMeta(track, pathname);
  return formatIpeDocumentTitle(`TEMU · ${meta.title}`);
}

function resolveTiktokDocumentTitle(pathname = '') {
  if (pathname === IPE_TIKTOK_GATE_PATH || pathname.startsWith(`${IPE_TIKTOK_GATE_PATH}?`)) {
    return formatIpeDocumentTitle(fixedUi('进入 TikTok 工作区'));
  }
  if (pathname === IPE_TIKTOK_BASE_PATH) {
    return formatIpeDocumentTitle(fixedUi('TikTok 工作区'));
  }
  const match = pathname.match(/^\/ipe\/tiktok\/(crossborder|local)(\/.*)?$/);
  if (!match) {
    return formatIpeDocumentTitle(fixedUi('TikTok 工作区'));
  }
  const track = match[1];
  const meta = resolveTiktokPageMeta(track, pathname);
  return formatIpeDocumentTitle(`TikTok · ${meta.title}`);
}

function resolveWorkbenchNavTitle(navId) {
  if (!navId || navId === WORKBENCH_HOME_NAV_ID) {
    return formatIpeDocumentTitle('工作台');
  }

  const tool = TOOL_NAV_ITEMS[navId];
  if (tool?.label) {
    return formatIpeDocumentTitle(tool.label);
  }

  const hub = NAV_HUBS[navId];
  if (hub?.label) {
    return formatIpeDocumentTitle(hub.label);
  }

  return formatIpeDocumentTitle('工作台');
}

const AI_IMAGE_DETAIL_NAV_TITLES = {
  imageCrop: '图案裁剪详情',
  imageEnhance: '超分提质详情',
  smartCutout: '智能抠图详情',
  stampExtract: '印花提取详情',
  img2img: '万能改图详情',
  aiOutpaint: 'AI 扩图详情',
  aiTextToImage: '文生图详情',
  aiCopywriting: 'AI 文案详情',
};

function resolveWorkbenchDocumentTitle(pathname = '', search = '') {
  const aiImageDetailMatch = pathname.match(/\/ai-image-detail\/([^/]+)/);
  if (aiImageDetailMatch) {
    const navId = decodeURIComponent(aiImageDetailMatch[1]);
    const title = AI_IMAGE_DETAIL_NAV_TITLES[navId] || 'AI 图片详情';
    return formatIpeDocumentTitle(title);
  }

  for (const [segment, title] of WORKBENCH_DETAIL_TITLES) {
    if (pathname.includes(`/${segment}/`) || pathname.endsWith(`/${segment}`)) {
      return formatIpeDocumentTitle(title);
    }
  }

  if (pathname === IPE_AGENT_CHAT_PATH || pathname.startsWith(`${IPE_AGENT_CHAT_PATH}/`)) {
    return formatIpeDocumentTitle('Agent');
  }

  if (pathname === IPE_CREDIT_CONSUMPTION_PATH || pathname.startsWith(`${IPE_CREDIT_CONSUMPTION_PATH}?`)) {
    return formatIpeDocumentTitle('IPE 豆消耗说明');
  }

  if (pathname === IPE_RECHARGE_PATH || pathname.startsWith(`${IPE_RECHARGE_PATH}?`)) {
    return formatIpeDocumentTitle('充值中心');
  }

  if (pathname === IPE_PLUGIN_INSTALL_PATH || pathname.startsWith(`${IPE_PLUGIN_INSTALL_PATH}?`)) {
    const title = i18n.t('pluginInstallPage.docTitle', { ns: 'workbench', defaultValue: '' })
      || (String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh')
        ? '插件安装'
        : 'Extension install');
    return formatIpeDocumentTitle(title);
  }

  const navId = resolveNavFromWorkbenchLocation({ pathname, search });
  return resolveWorkbenchNavTitle(navId);
}

export function resolveDocumentTitle(pathname = '', search = '') {
  const path = pathname || '';

  if (path === '/ipe/commerce' || path.startsWith('/ipe/commerce?')) {
    return formatIpeDocumentTitle('选择电商平台');
  }

  if (isTemuPlatformPath(path)) {
    return resolveTemuDocumentTitle(path);
  }

  if (isTiktokPlatformPath(path)) {
    return resolveTiktokDocumentTitle(path);
  }

  if (isSheinPlatformPath(path)) {
    return resolveSheinDocumentTitle(path);
  }

  if (path.startsWith('/ipe/admin')) {
    return formatIpeDocumentTitle('Admin 控制台');
  }

  if (path.startsWith('/ipe/agent')) {
    return formatIpeDocumentTitle('Agent 控制台');
  }

  if (path === IPE_MANUAL_LOGIN_PATH || path.startsWith(`${IPE_MANUAL_LOGIN_PATH}?`)) {
    return 'IPE 使用手册';
  }

  if (path === IPE_MANUAL_HOME_PATH || path.startsWith(`${IPE_MANUAL_HOME_PATH}/`)) {
    return 'IPE 使用手册';
  }

  if (path === '/legal/terms') {
    const title = i18n.t('legal.termsDocTitle', { ns: 'login', defaultValue: '' })
      || (String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh')
        ? '用户服务协议'
        : 'User Service Agreement');
    return formatIpeDocumentTitle(title);
  }

  if (path === '/legal/privacy') {
    const title = i18n.t('legal.privacyDocTitle', { ns: 'login', defaultValue: '' })
      || (String(i18n.resolvedLanguage || i18n.language || '').toLowerCase().startsWith('zh')
        ? '隐私政策'
        : 'Privacy Policy');
    return formatIpeDocumentTitle(title);
  }

  if (isIpeLoginPath(path)) {
    return formatIpeDocumentTitle('登录');
  }

  if (path === '/ipe-design' || path.startsWith('/ipe-design?')) {
    return formatIpeDocumentTitle('设计预览');
  }

  if (path === '/' || path === '/home') {
    return formatIpeDocumentTitle('智能商品图像引擎');
  }

  if (isIpeWorkbenchPath(path)) {
    return resolveWorkbenchDocumentTitle(path, search);
  }

  return DEFAULT_DOCUMENT_TITLE;
}

export function setDocumentTitle(pageTitle) {
  document.title = formatIpeDocumentTitle(pageTitle);
}
